import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, getToken, API } from "../api";
import { useAuth } from "../auth";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { StatusBadge, PriorityBadge } from "../components/Badges";
import PositionFormDialog from "../components/PositionFormDialog";
import { toast } from "sonner";
import {
  ArrowLeft, Pencil, Trash2, PauseCircle, Upload, Download, FileText,
  Send, MessageCircle, History, Paperclip, Building2, MapPin, User, Calendar,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function PositionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [position, setPosition] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [comments, setComments] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [openEdit, setOpenEdit] = useState(false);
  const [newComment, setNewComment] = useState("");
  const fileRef = useRef();
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeCv, setActiveCv] = useState(null); // attachment_id when filtering CV-specific comments
  const [commentScope, setCommentScope] = useState("general"); // 'general' or attachment_id

  const canEdit = user?.role === "Admin" || user?.role === "HR";
  const canSeeBudget = user?.role === "Admin" || user?.role === "Management";

  const loadAll = async () => {
    const [p, a, c, t] = await Promise.all([
      api.get(`/positions/${id}`),
      api.get(`/positions/${id}/attachments`),
      api.get(`/positions/${id}/comments`),
      api.get(`/positions/${id}/timeline`),
    ]);
    setPosition(p.data); setAttachments(a.data); setComments(c.data); setTimeline(t.data);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  const onUpload = async (e) => {
    const list = e.target.files;
    if (!list || list.length === 0) return;
    await uploadFiles(Array.from(list));
    e.target.value = "";
  };

  const uploadFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      if (files.length === 1) {
        const fd = new FormData();
        fd.append("file", files[0]);
        await api.post(`/positions/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("File uploaded");
      } else {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        const r = await api.post(`/positions/${id}/attachments/bulk`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`Uploaded ${r.data.created.length} file(s)${r.data.errors.length ? ` (${r.data.errors.length} failed)` : ""}`);
      }
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!canEdit) return;
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) uploadFiles(files);
  };

  const onDeleteAttachment = async (aid) => {
    try {
      await api.delete(`/attachments/${aid}`);
      toast.success("Attachment removed");
      loadAll();
    } catch (err) { toast.error("Failed"); }
  };

  const downloadAttachment = (a) => {
    const url = `${API}/attachments/${a.id}/download?auth=${encodeURIComponent(getToken())}`;
    window.open(url, "_blank");
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      const isCv = commentScope !== "general";
      await api.post(`/positions/${id}/comments`, {
        text: newComment,
        type: isCv ? "cv" : "general",
        attachment_id: isCv ? commentScope : null,
      });
      setNewComment("");
      loadAll();
    } catch { toast.error("Failed to add comment"); }
  };

  const onHold = async () => {
    try {
      await api.post(`/positions/${id}/hold`);
      toast.success("Marked On Hold");
      loadAll();
    } catch { toast.error("Failed"); }
  };

  const onDelete = async () => {
    try {
      await api.delete(`/positions/${id}`);
      toast.success("Position deleted");
      nav("/");
    } catch { toast.error("Failed"); }
  };

  if (!position) {
    return <div className="text-muted-foreground p-8">Loading…</div>;
  }

  const overviewFields = [
    ["Position ID", position.position_code || "—"],
    ["Designation", position.designation],
    ["Designation Offer", position.designation_offer || "—"],
    ["Entity", position.brand], ["Brand", position.product_brand || "—"],
    ["Unit", position.unit || "—"], ["Business Unit", position.business_unit || "—"],
    ["Category", position.category || "—"],
    ["Status", <StatusBadge key="s" status={position.status} />],
    ["TRF Raised", <span key="trf" className={`text-xs font-medium px-2 py-0.5 rounded-full ${position.trf_raised === "Yes" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{position.trf_raised}</span>],
    ["Priority", <PriorityBadge key="p" priority={position.priority} />],
    ["Department", position.department],
    ["Country", position.country || "—"],
    ["City", position.city || position.location || "—"],
    ["Region", position.region],
    ["Recruiter", position.recruiter], ["Hiring Manager", position.hiring_manager],
    ["Candidate", position.candidate_name || "—"], ["Gender", position.gender || "—"],
    ["Age", position.age || "—"],
    ["Experience", position.experience || "—"],
    ["Qualification", position.qualification || "—"],
    ["Budget Type", position.budget_type],
    ...(canSeeBudget ? [["Approximate Budget", position.approximate_budget || "—"]] : []),
    ["Employment Type", position.employment_type],
    ["Replacement Of", position.replacement_of || "—"],
    ["Rehiring Name of Employee", position.rehiring_employee_name || "—"],
    ["Referrer Name", position.referrer_name || "—"],
    ["Referral Amount", position.referrer_amount != null && position.referrer_amount !== "" ? position.referrer_amount : "—"],
    ["TRF Date", fmt(position.trf_date)], ["TRF Hold Date", fmt(position.trf_hold_date) || "—"],
    ["TRF Reopen Date", fmt(position.trf_reopen_date) || "—"], ["Offer Date", fmt(position.offer_date)],
    ["Date of Joining", fmt(position.date_of_joining)], ["Notice Period", position.notice_period || "—"],
    ["# Positions", position.number_of_positions], ["Source", position.source_channel || "—"],
    ["Offer Stage", position.offer_stage || "—"], ["Interview Status", position.interview_status || "—"],
    ["Created", fmt(position.created_at)], ["Updated", fmt(position.updated_at)],
  ];

  return (
    <div className="space-y-6">
      <button onClick={() => nav(-1)} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
            <span className="font-mono font-semibold text-primary mr-2" data-testid="position-code">{position.position_code}</span>
            {position.brand}
          </div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight mt-1">{position.designation}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {position.city || position.location || "—"}</span>
            <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {position.recruiter}</span>
            <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {position.department}</span>
            <StatusBadge status={position.status} />
            <PriorityBadge priority={position.priority} />
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpenEdit(true)} data-testid="edit-position-btn">
              <Pencil className="w-4 h-4 mr-2" /> Edit
            </Button>
            <Button variant="outline" onClick={onHold} data-testid="hold-btn">
              <PauseCircle className="w-4 h-4 mr-2" /> On Hold
            </Button>
            {user.role === "Admin" && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="text-destructive hover:text-destructive" data-testid="delete-position-btn">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this position?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently removes the position, attachments and comments.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Tabs defaultValue="overview">
            <TabsList className="bg-card border border-border">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="attachments" data-testid="tab-attachments">
                <Paperclip className="w-3.5 h-3.5 mr-1.5" />Attachments ({attachments.length})
              </TabsTrigger>
              <TabsTrigger value="comments" data-testid="tab-comments">
                <MessageCircle className="w-3.5 h-3.5 mr-1.5" />Comments ({comments.length})
              </TabsTrigger>
              <TabsTrigger value="timeline" data-testid="tab-timeline">
                <History className="w-3.5 h-3.5 mr-1.5" />Timeline
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4">
              <div className="rounded-xl border border-border bg-card p-6">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                  {overviewFields.map(([k, v]) => (
                    <div key={k} className="border-b border-border/60 pb-3">
                      <dt className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">{k}</dt>
                      <dd className="text-sm">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </TabsContent>

            <TabsContent value="attachments" className="mt-4 space-y-4">
              {canEdit && (
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  className={`rounded-xl border-2 border-dashed transition-colors p-8 text-center ${
                    dragOver ? "border-primary bg-primary/5" : "border-border bg-card"
                  }`}
                  data-testid="attachment-dropzone"
                >
                  <Upload className={`w-8 h-8 mx-auto mb-2 ${dragOver ? "text-primary" : "text-muted-foreground/60"}`} />
                  <div className="text-sm font-medium mb-1">
                    {uploading ? "Uploading…" : "Drop CV / files here"}
                  </div>
                  <div className="text-xs text-muted-foreground mb-3">
                    or click to browse · multiple files supported · max 15MB each
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={onUpload}
                    data-testid="attachment-input"
                  />
                  <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-attachment-btn">
                    <Upload className="w-4 h-4 mr-2" /> Browse files
                  </Button>
                </div>
              )}
              {attachments.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground">
                  <Paperclip className="w-8 h-8 mx-auto opacity-40 mb-2" />
                  No attachments yet.
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {attachments.map((a) => {
                    const cvComments = comments.filter((c) => c.attachment_id === a.id);
                    return (
                    <div key={a.id} className="rounded-xl border border-border bg-card p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                          <FileText className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{a.filename}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {Math.round(a.size / 1024)} KB · {a.uploaded_by_name} · {fmt(a.created_at)}
                          </div>
                          <div className="flex flex-wrap gap-2 mt-3">
                            <Button size="sm" variant="outline" onClick={() => downloadAttachment(a)}>
                              <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setActiveCv(a.id); setCommentScope(a.id); document.querySelector('[data-testid="tab-comments"]')?.click(); }} data-testid={`cv-comments-${a.id}`}>
                              <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> {cvComments.length} comment{cvComments.length !== 1 ? "s" : ""}
                            </Button>
                            {canEdit && (
                              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDeleteAttachment(a.id)}>
                                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );})}
                </div>
              )}
            </TabsContent>

            <TabsContent value="comments" className="mt-4 space-y-4">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground">Post to:</span>
                  <select
                    className="text-xs bg-muted/40 border border-border rounded px-2 py-1"
                    value={commentScope}
                    onChange={(e) => setCommentScope(e.target.value)}
                    data-testid="comment-scope-select"
                  >
                    <option value="general">General</option>
                    {attachments.map((a) => (
                      <option key={a.id} value={a.id}>{`CV: ${a.filename}`}</option>
                    ))}
                  </select>
                </div>
                <Textarea
                  data-testid="comment-input"
                  placeholder={commentScope === "general" ? "Add a comment…" : "Comment on this CV…"}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="min-h-[80px]"
                />
                <div className="flex justify-end mt-2">
                  <Button onClick={addComment} disabled={!newComment.trim()} data-testid="add-comment-btn">
                    <Send className="w-4 h-4 mr-2" /> Post
                  </Button>
                </div>
              </div>

              {/* Filter chips */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">Filter:</span>
                <button
                  onClick={() => setActiveCv(null)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${activeCv === null ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-foreground/70 hover:bg-muted"}`}
                >
                  All ({comments.length})
                </button>
                <button
                  onClick={() => setActiveCv("general")}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${activeCv === "general" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-foreground/70 hover:bg-muted"}`}
                >
                  General ({comments.filter((c) => !c.attachment_id).length})
                </button>
                {attachments.map((a) => {
                  const n = comments.filter((c) => c.attachment_id === a.id).length;
                  if (!n) return null;
                  return (
                    <button
                      key={a.id}
                      onClick={() => setActiveCv(a.id)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${activeCv === a.id ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-foreground/70 hover:bg-muted"}`}
                    >
                      <FileText className="inline w-3 h-3 mr-1 -mt-0.5" />{a.filename} ({n})
                    </button>
                  );
                })}
              </div>

              {(() => {
                const filtered = activeCv === null ? comments
                  : activeCv === "general" ? comments.filter((c) => !c.attachment_id)
                  : comments.filter((c) => c.attachment_id === activeCv);
                if (filtered.length === 0) return <div className="text-sm text-muted-foreground text-center py-6">No comments yet.</div>;
                return (
                  <div className="space-y-3">
                    {filtered.map((c) => {
                      const att = c.attachment_id ? attachments.find((a) => a.id === c.attachment_id) : null;
                      return (
                        <div key={c.id} className="rounded-xl border border-border bg-card p-4">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium">{c.user_name}</div>
                              {att && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] bg-primary/10 text-primary font-medium">
                                  <FileText className="w-3 h-3 mr-1" />{att.filename}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">{fmt(c.timestamp)}</div>
                          </div>
                          <div className="text-sm text-foreground/85 whitespace-pre-wrap">{c.text}</div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </TabsContent>

            <TabsContent value="timeline" className="mt-4">
              <div className="rounded-xl border border-border bg-card p-6">
                {timeline.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-6">No activity yet.</div>
                ) : (
                  <ol className="relative border-l-2 border-border ml-3 space-y-6">
                    {timeline.map((t) => (
                      <li key={t.id} className="ml-6">
                        <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-primary border-2 border-card" />
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-0.5">
                          <Calendar className="w-3 h-3" /> {fmt(t.timestamp)}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">{t.actor_name}</span> · {t.description}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Key Meta</div>
            <Meta label="Status" value={<StatusBadge status={position.status} />} />
            <Meta label="Priority" value={<PriorityBadge priority={position.priority} />} />
            <Meta label="Hiring Manager" value={position.hiring_manager} />
            <Meta label="Recruiter" value={position.recruiter} />
            <Meta label="Budget Type" value={position.budget_type} />
            <Meta label="Employment Type" value={position.employment_type} />
            <Meta label="DOJ" value={fmt(position.date_of_joining) || "—"} />
            <Meta label="Notice Period" value={position.notice_period || "—"} />
          </div>
        </aside>
      </div>

      <PositionFormDialog open={openEdit} onOpenChange={setOpenEdit} position={position} onSaved={() => { setOpenEdit(false); loadAll(); }} />
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="flex items-start justify-between py-2 border-b border-border/60 last:border-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium text-right">{value}</div>
    </div>
  );
}

function fmt(s) {
  if (!s) return "";
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return s; }
}
